/**
 * The wording and the selection arithmetic behind removing companies from a flag.
 *
 * Kept apart from the components that render it for the same reason the rest of this feature splits
 * its logic out: the phrasing of a destructive confirmation is worth asserting on directly, without a
 * DOM in the way.
 */

/** What the bulk-remove bar says about the current selection. */
export function selectionSummaryLabel(selectedCount: number): string {
	if (selectedCount === 0) {
		// An instruction rather than "0 companies selected": the bar appears the moment bulk remove is
		// started, before anything has been picked, so its first job is to say what to do next.
		return 'Select companies to remove';
	}
	return selectedCount === 1 ? '1 company selected' : `${selectedCount} companies selected`;
}

/**
 * The bulk-remove bar's confirm button.
 *
 * The count sits on the bar immediately to the left of this button, so repeating it for a single
 * company would print "1" twice in one short sentence. It earns its place once there are several,
 * where it is what tells the operator the scale of what they are about to do.
 */
export function removeSelectedLabel(selectedCount: number): string {
	return selectedCount > 1 ? `Remove ${selectedCount} companies` : 'Remove selected';
}

/** The remove-all dialog's confirm button, which names the scale of the removal. */
export function removeAllLabel(assignedCount: number): string {
	return `Remove all ${assignedCount}`;
}

/**
 * The remove-all dialog's explanation.
 *
 * Names the flag, because this dialog can be reached from any flag's detail page and the flag name is
 * the one thing that makes the sentence specific. Says individual users go too, since a company can
 * appear in the table holding the flag only through named users and clearing "all companies" takes
 * those with it.
 */
export function removeAllBody(assignedCount: number, flagName: string): string {
	const companies = assignedCount === 1 ? 'company' : 'companies';
	return `All ${assignedCount} ${companies} currently assigned to “${flagName}” will be unassigned, including any individual users.`;
}

/** The selection with one company added or taken away. Always a new array. */
export function toggleCompanySelection(selectedCompanyIds: readonly string[], companyId: string): string[] {
	return selectedCompanyIds.includes(companyId)
		? selectedCompanyIds.filter(id => id !== companyId)
		: [...selectedCompanyIds, companyId];
}

/**
 * Whether the select-all box should read as ticked.
 *
 * False for an empty table: there is nothing to have selected, and a ticked box there would claim a
 * selection of nothing.
 *
 * Compared by membership rather than by count, because the two can disagree: the row edit control
 * stays live while companies are being picked, and revoking a company from the rollout modal takes its
 * row out of the table without touching the selection. A selection of the same size as what is left
 * would then read as complete while a visible row sat unticked, and pressing the box would clear the
 * selection instead of completing it.
 */
export function areAllCompaniesSelected(selectedCompanyIds: readonly string[], allCompanyIds: readonly string[]): boolean {
	return allCompanyIds.length > 0 && allCompanyIds.every(companyId => selectedCompanyIds.includes(companyId));
}

/**
 * Everything, or nothing.
 *
 * Copies `allCompanyIds` rather than handing it back, so the selection state can never end up holding
 * the same array the table is rendering from.
 */
export function toggleAllCompanies(selectedCompanyIds: readonly string[], allCompanyIds: readonly string[]): string[] {
	return areAllCompaniesSelected(selectedCompanyIds, allCompanyIds) ? [] : [...allCompanyIds];
}
